import { IAssetCategoryResponse } from '@kcftech/prometheustypescriptapiclient';
import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { usePermissions } from 'contexts/PermissionsContext';
import { useAssetCategories } from 'custom_hooks/data_hooks/asset/useAssetCategories';
import { useAssetCategoryParameters } from 'custom_hooks/data_hooks/asset/useAssetCategoryParameters';
import { useEffectSkipFirst } from 'custom_hooks/useEffectSkipFirst';
import { AssetCategoryDetailTree } from 'pages/Settings/AssetType/AssetCategoryDetailTree';
import { AssetParameterDetailTree } from 'pages/Settings/AssetType/AssetParameterDetailTree';
import {
    AssetParameterCreationInfo,
    CreateAssetParameterModal,
} from 'pages/Settings/AssetType/CreateAssetParameterModal';
import {
    Column,
    ColumnContent,
    ColumnHeader,
    ColumnsSection,
    ScrollableContent,
    ScrollableInnerContent,
} from 'pages/Settings/AssetType/styled';
import { CategoryTreeData } from 'pages/Settings/AssetType/types';
import { useEffect, useState } from 'react';
import { buildTreeData, createTree, filterTreeData } from 'shared/data_tree/tree';
import { addMissingItems, excludeNullAndUndefined, toggleArrayItem } from 'utils/array';
import { getChildCategories } from 'utils/asset';

const rootCategoryId = 'root';

const buildCategoryTreeData = (allCategories: IAssetCategoryResponse[] = []) =>
    buildTreeData<CategoryTreeData>({
        data: [{}],
        dataKey: (item) => item.category?.Id.toString() ?? rootCategoryId,
        children: (parent) =>
            getChildCategories({
                allCategories,
                parentCategoryId: parent.category?.Id,
            }).map<CategoryTreeData>((category) => ({ category })),
    });

export type AssetCategoryParameterDetailsProps = Readonly<{
    assetTypeId: number;
}>;

export function AssetCategoryParameterDetails({
    assetTypeId,
}: AssetCategoryParameterDetailsProps) {
    const { canEditAssetType: canEditAssetParameter } = usePermissions();
    const [filterText, setFilterText] = useState('');

    const [tree, setTree] = useState(() => createTree<CategoryTreeData>());
    const [filteredTree, setFilteredTree] = useState(tree);
    const [selectedCategory, setSelectedCategory] = useState<IAssetCategoryResponse>();
    const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([
        rootCategoryId,
    ]);

    const [
        parameterCreationInfo,
        setParameterCreationInfo,
    ] = useState<AssetParameterCreationInfo>();

    const {
        results: categories,
        isLoading: isLoadingCategories,
        refetch: refetchCategories,
    } = useAssetCategories(assetTypeId);

    const {
        data: parameters,
        isLoading: isLoadingParameters,
        refetch: refetchParameters,
    } = useAssetCategoryParameters({
        categoryId: selectedCategory?.Id,
        removeDuplicates: true,
        includeInherited: false,
    });

    const onCategoryCreateOrUpdate = (category: IAssetCategoryResponse) => {
        if (category.ParentCategory?.AssetCategoryParentId) {
            setExpandedCategoryIds((current) =>
                addMissingItems(
                    current,
                    excludeNullAndUndefined([
                        category.ParentCategory?.AssetCategoryParentId.toString(),
                    ])
                )
            );
        }
        setSelectedCategory(category);
        refetchCategories();
    };

    const onCreateParameterClick = () => {
        if (selectedCategory && parameters) {
            setParameterCreationInfo({
                category: selectedCategory,
                siblingParameters: parameters,
            });
        }
    };

    const onParameterChange = () => {
        refetchCategories();
        refetchParameters();
    };

    const onCreateParameterClose = (saved: boolean) => {
        setParameterCreationInfo(undefined);
        if (saved) {
            onParameterChange();
        }
    };

    const onFilterTextChange = (newFilterText: string) => {
        setFilterText(newFilterText.toLowerCase());
    };

    useEffect(() => {
        const trimmedFilterText = filterText.trim();

        if (trimmedFilterText) {
            const { filteredTree: newTree, expandedIds } = filterTreeData({
                tree,
                where: (node) =>
                    node.data.category?.ShortName?.toLowerCase().includes(
                        trimmedFilterText
                    ) ?? false,
            });

            setFilteredTree(newTree);
            setExpandedCategoryIds(expandedIds);
        } else {
            setFilteredTree(tree);
        }
    }, [filterText, tree]);

    useEffect(() => {
        if (categories) {
            setTree(createTree(buildCategoryTreeData(categories)));
        }
    }, [categories]);

    useEffectSkipFirst(() => {
        setFilterText('');
        setSelectedCategory(undefined);
    }, [assetTypeId, setSelectedCategory]);

    return (
        <>
            <SpinnerArea isLoading={isLoadingCategories || isLoadingParameters}>
                <ColumnsSection>
                    <Column>
                        <ColumnHeader>
                            <h4>Categories</h4>
                            <DebouncedSearchInput
                                value={filterText}
                                onChange={onFilterTextChange}
                                placeholder="Search Categories"
                            />
                        </ColumnHeader>
                        <ColumnContent>
                            <ScrollableContent>
                                <ScrollableInnerContent>
                                    {categories && (
                                        <AssetCategoryDetailTree
                                            className="mt-10"
                                            filterText={filterText}
                                            assetTypeId={assetTypeId}
                                            allCategories={categories}
                                            categoryTree={filteredTree}
                                            expandedCategoryIds={expandedCategoryIds}
                                            selectedCategory={selectedCategory}
                                            onCategorySelect={setSelectedCategory}
                                            onExpandChange={(event) =>
                                                setExpandedCategoryIds((current) =>
                                                    toggleArrayItem(
                                                        current,
                                                        event.node.id
                                                    )
                                                )
                                            }
                                            onCategoryCreate={onCategoryCreateOrUpdate}
                                            onCategoryUpdate={onCategoryCreateOrUpdate}
                                            onCategoryDelete={refetchCategories}
                                        />
                                    )}
                                </ScrollableInnerContent>
                            </ScrollableContent>
                        </ColumnContent>
                    </Column>
                    <Column>
                        <ColumnHeader className="flex-row flex-nowrap justify-between align-center">
                            {selectedCategory && (
                                <>
                                    <h4>Parameters: {selectedCategory.Name}</h4>
                                    {canEditAssetParameter && (
                                        <CreateButton
                                            appearance="subtle"
                                            onClick={onCreateParameterClick}
                                            className="ml-10"
                                        />
                                    )}
                                </>
                            )}
                        </ColumnHeader>
                        <ColumnContent>
                            <ScrollableContent>
                                <ScrollableInnerContent>
                                    {selectedCategory && parameters && (
                                        <AssetParameterDetailTree
                                            category={selectedCategory}
                                            parameters={parameters}
                                            onChange={onParameterChange}
                                        />
                                    )}
                                </ScrollableInnerContent>
                            </ScrollableContent>
                        </ColumnContent>
                    </Column>
                </ColumnsSection>
            </SpinnerArea>

            {parameterCreationInfo && (
                <CreateAssetParameterModal
                    {...parameterCreationInfo}
                    onClose={onCreateParameterClose}
                />
            )}
        </>
    );
}
